import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Badge } from '../../../components/ui/badge';
import {
  Plus, Loader2, Trash2, ArrowLeft, CheckCircle2, XCircle,
  Clock, BarChart3, Award, CircleDot, HelpCircle, X, Timer,
  Edit3, FileText, Lock, Unlock, Send, Mail
} from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export default function CampusQuizzes({ spaceId, esCoordinadorODocente, token }) {
  const headers = { Authorization: `Bearer ${token}` };
  const [quizzes, setQuizzes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('list'); // list | create | take | results | correct
  const [selectedQuiz, setSelectedQuiz] = useState(null);

  // Create/Edit form
  const [form, setForm] = useState({ titulo: '', descripcion: '', tiempo_limite: '', intentos_permitidos: 1, mostrar_respuestas: true, aprobacion_min_correctas: '' });
  const [preguntas, setPreguntas] = useState([]);
  const [saving, setSaving] = useState(false);
  const [editingQuizId, setEditingQuizId] = useState(null);

  // Take quiz
  const [respuestas, setRespuestas] = useState({});
  const [submittingQuiz, setSubmittingQuiz] = useState(false);
  const [resultado, setResultado] = useState(null);

  // Results
  const [resultados, setResultados] = useState(null);
  const [loadingResults, setLoadingResults] = useState(false);
  const [intentoDetalle, setIntentoDetalle] = useState(null); // intento elegido para ver respuestas
  const [enviarModal, setEnviarModal] = useState(null); // { inscripcion, participante }
  const [enviarForm, setEnviarForm] = useState({ tipo: 'certificado', mensaje_adicional: '' });
  const [enviandoEmail, setEnviandoEmail] = useState(false);

  // Correction tray (docente)
  const [pendientes, setPendientes] = useState(null);
  const [loadingPendientes, setLoadingPendientes] = useState(false);
  const [intentoEnCorreccion, setIntentoEnCorreccion] = useState(null);
  const [correccionDraft, setCorreccionDraft] = useState({}); // {pregunta_id: {puntos_obtenidos, feedback}}
  const [savingCorr, setSavingCorr] = useState(false);

  const abrirBandejaCorreccion = async (quiz) => {
    setSelectedQuiz(quiz);
    setView('correct');
    setLoadingPendientes(true);
    try {
      const res = await axios.get(`${API_URL}/campus/espacios/${spaceId}/quizzes/${quiz.id}/pendientes`, { headers });
      setPendientes(res.data);
    } catch (err) {
      alert(err.response?.data?.detail || 'Error al cargar pendientes');
      setView('list');
    } finally {
      setLoadingPendientes(false);
    }
  };

  const iniciarCorreccionIntento = (intento) => {
    const preguntasMap = {};
    (pendientes.quiz.preguntas || []).forEach(p => { preguntasMap[p.id] = p; });
    const draft = {};
    (intento.respuestas || []).filter(r => r.tipo === 'texto_abierto').forEach(r => {
      const pregunta = preguntasMap[r.pregunta_id];
      draft[r.pregunta_id] = {
        puntos_obtenidos: r.corregida ? r.puntos_obtenidos : (pregunta?.puntos || 1),
        feedback: r.feedback || '',
        max: pregunta?.puntos || 1,
      };
    });
    setCorreccionDraft(draft);
    setIntentoEnCorreccion(intento);
  };

  const guardarCorreccion = async () => {
    if (!intentoEnCorreccion) return;
    setSavingCorr(true);
    try {
      const payload = {
        correcciones: Object.entries(correccionDraft).map(([pregunta_id, v]) => ({
          pregunta_id,
          puntos_obtenidos: parseInt(v.puntos_obtenidos) || 0,
          feedback: v.feedback || null,
        }))
      };
      await axios.post(
        `${API_URL}/campus/espacios/${spaceId}/quizzes/${selectedQuiz.id}/intentos/${intentoEnCorreccion.id}/corregir`,
        payload, { headers }
      );
      setIntentoEnCorreccion(null);
      setCorreccionDraft({});
      // Refrescar pendientes
      const res = await axios.get(`${API_URL}/campus/espacios/${spaceId}/quizzes/${selectedQuiz.id}/pendientes`, { headers });
      setPendientes(res.data);
    } catch (err) {
      alert(err.response?.data?.detail || 'Error al guardar corrección');
    } finally {
      setSavingCorr(false);
    }
  };

  const fetchQuizzes = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/campus/espacios/${spaceId}/quizzes`, { headers });
      setQuizzes(res.data);
    } catch (err) {
      console.error('Error cargando quizzes:', err);
    } finally {
      setLoading(false);
    }
  }, [spaceId]);

  useEffect(() => { fetchQuizzes(); }, [fetchQuizzes]);

  // --- Create Quiz ---
  const addPregunta = (tipo = 'choice') => {
    if (tipo === 'texto_abierto') {
      setPreguntas([...preguntas, {
        texto: '', puntos: 1, tipo: 'texto_abierto', opciones: []
      }]);
    } else {
      setPreguntas([...preguntas, {
        texto: '', puntos: 1, tipo: 'choice',
        opciones: [
          { texto: '', es_correcta: true },
          { texto: '', es_correcta: false },
        ]
      }]);
    }
  };

  const updatePregunta = (idx, field, value) => {
    setPreguntas(prev => prev.map((p, i) => i === idx ? { ...p, [field]: value } : p));
  };

  const addOpcion = (pIdx) => {
    setPreguntas(prev => prev.map((p, i) => i === pIdx
      ? { ...p, opciones: [...p.opciones, { texto: '', es_correcta: false }] }
      : p
    ));
  };

  const updateOpcion = (pIdx, oIdx, field, value) => {
    setPreguntas(prev => prev.map((p, pi) => {
      if (pi !== pIdx) return p;
      const opciones = p.opciones.map((o, oi) => {
        if (field === 'es_correcta' && value === true) {
          return oi === oIdx ? { ...o, es_correcta: true } : { ...o, es_correcta: false };
        }
        return oi === oIdx ? { ...o, [field]: value } : o;
      });
      return { ...p, opciones };
    }));
  };

  const removePregunta = (idx) => {
    setPreguntas(prev => prev.filter((_, i) => i !== idx));
  };

  const removeOpcion = (pIdx, oIdx) => {
    setPreguntas(prev => prev.map((p, i) => i === pIdx
      ? { ...p, opciones: p.opciones.filter((_, oi) => oi !== oIdx) }
      : p
    ));
  };

  const guardarQuiz = async () => {
    if (!form.titulo.trim()) { alert('Ingresá un título'); return; }
    if (preguntas.length === 0) { alert('Agregá al menos una pregunta'); return; }
    for (let i = 0; i < preguntas.length; i++) {
      if (!preguntas[i].texto.trim()) { alert(`La pregunta ${i + 1} no tiene texto`); return; }
      if ((preguntas[i].tipo || 'choice') === 'texto_abierto') {
        continue; // texto abierto no requiere opciones
      }
      if (preguntas[i].opciones.length < 2) { alert(`La pregunta ${i + 1} necesita al menos 2 opciones`); return; }
      const vacias = preguntas[i].opciones.filter(o => !o.texto.trim());
      if (vacias.length > 0) { alert(`Hay opciones vacías en la pregunta ${i + 1}`); return; }
      if (!preguntas[i].opciones.some(o => o.es_correcta)) { alert(`La pregunta ${i + 1} no tiene respuesta correcta`); return; }
    }

    setSaving(true);
    try {
      const aprobMin = form.aprobacion_min_correctas === '' || form.aprobacion_min_correctas === null
        ? null
        : Math.max(0, Math.min(preguntas.length, parseInt(form.aprobacion_min_correctas) || 0));
      const payload = {
        ...form,
        tiempo_limite: form.tiempo_limite ? parseInt(form.tiempo_limite) : null,
        intentos_permitidos: parseInt(form.intentos_permitidos) || 1,
        aprobacion_min_correctas: aprobMin,
        preguntas,
      };
      if (editingQuizId) {
        await axios.put(`${API_URL}/campus/espacios/${spaceId}/quizzes/${editingQuizId}`, payload, { headers });
      } else {
        await axios.post(`${API_URL}/campus/espacios/${spaceId}/quizzes`, payload, { headers });
      }
      cancelarEdicionQuiz();
      fetchQuizzes();
    } catch (err) {
      alert(err.response?.data?.detail || (editingQuizId ? 'Error al actualizar el examen' : 'Error al crear examen'));
    } finally {
      setSaving(false);
    }
  };

  const iniciarEdicionQuiz = async (quiz) => {
    try {
      const res = await axios.get(`${API_URL}/campus/espacios/${spaceId}/quizzes/${quiz.id}`, { headers });
      const q = res.data;
      setForm({
        titulo: q.titulo || '',
        descripcion: q.descripcion || '',
        tiempo_limite: q.tiempo_limite || '',
        intentos_permitidos: q.intentos_permitidos || 1,
        mostrar_respuestas: q.mostrar_respuestas !== false,
        aprobacion_min_correctas: q.aprobacion_min_correctas ?? '',
      });
      setPreguntas((q.preguntas || []).map(p => ({
        id: p.id,
        texto: p.texto || '',
        puntos: p.puntos || 1,
        tipo: p.tipo || 'choice',
        opciones: (p.opciones || []).map(o => ({
          id: o.id,
          texto: o.texto || '',
          es_correcta: !!o.es_correcta,
        })),
      })));
      setEditingQuizId(quiz.id);
      setView('create');
    } catch (err) {
      alert(err.response?.data?.detail || 'Error al cargar el examen');
    }
  };

  const cancelarEdicionQuiz = () => {
    setEditingQuizId(null);
    setView('list');
    setForm({ titulo: '', descripcion: '', tiempo_limite: '', intentos_permitidos: 1, mostrar_respuestas: true, aprobacion_min_correctas: '' });
    setPreguntas([]);
  };

  // --- Take Quiz ---
  const iniciarQuiz = async (quiz) => {
    try {
      const res = await axios.get(`${API_URL}/campus/espacios/${spaceId}/quizzes/${quiz.id}`, { headers });
      setSelectedQuiz(res.data);
      setRespuestas({});
      setResultado(null);
      setView('take');
    } catch (err) {
      alert(err.response?.data?.detail || 'Error al cargar quiz');
    }
  };

  const enviarRespuestas = async () => {
    const quiz = selectedQuiz;
    const totalPreguntas = quiz.preguntas?.length || 0;
    const totalRespondidas = Object.keys(respuestas).length;

    if (totalRespondidas < totalPreguntas) {
      if (!window.confirm(`Respondiste ${totalRespondidas} de ${totalPreguntas} preguntas. ¿Enviar igual?`)) return;
    }

    setSubmittingQuiz(true);
    try {
      const payload = {
        respuestas: Object.entries(respuestas).map(([pregunta_id, val]) => {
          // val puede ser { opcion_id } o { texto } según tipo
          if (typeof val === 'string' && val.startsWith('TXT:')) {
            return { pregunta_id, texto: val.slice(4) };
          }
          return { pregunta_id, opcion_id: val };
        })
      };
      const res = await axios.post(
        `${API_URL}/campus/espacios/${spaceId}/quizzes/${quiz.id}/responder`,
        payload, { headers }
      );
      setResultado(res.data);
      fetchQuizzes();
    } catch (err) {
      alert(err.response?.data?.detail || 'Error al enviar respuestas');
    } finally {
      setSubmittingQuiz(false);
    }
  };

  // --- Results ---
  const verResultados = async (quiz) => {
    setSelectedQuiz(quiz);
    setLoadingResults(true);
    setView('results');
    try {
      const res = await axios.get(`${API_URL}/campus/espacios/${spaceId}/quizzes/${quiz.id}/resultados`, { headers });
      setResultados(res.data);
    } catch (err) {
      console.error('Error cargando resultados:', err);
    } finally {
      setLoadingResults(false);
    }
  };

  const toggleEstadoQuiz = async (quiz) => {
    const nuevoEstado = quiz.estado === 'cerrada' ? 'publicada' : 'cerrada';
    const msg = nuevoEstado === 'cerrada'
      ? '¿Cerrar el examen? Los estudiantes ya no podrán realizarlo (los intentos anteriores se mantienen).'
      : '¿Reabrir el examen? Los estudiantes podrán volver a realizarlo.';
    if (!window.confirm(msg)) return;
    try {
      await axios.put(`${API_URL}/campus/espacios/${spaceId}/quizzes/${quiz.id}`,
        { estado: nuevoEstado },
        { headers });
      fetchQuizzes();
    } catch (err) {
      alert(err.response?.data?.detail || 'Error al cambiar el estado del examen');
    }
  };

  // Actualizar la nota / estado de aprobación de la inscripción vinculada
  const actualizarNotaInscripcion = async (inscripcionId, payload) => {
    try {
      const token = localStorage.getItem('token');
      await axios.patch(
        `${API_URL}/capacitaciones/admin/inscriptos/${inscripcionId}/nota`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      // Recargar detalle para reflejar el cambio
      if (selectedQuiz) {
        const res = await axios.get(`${API_URL}/campus/espacios/${spaceId}/quizzes/${selectedQuiz.id}/resultados`, { headers });
        setResultados(res.data);
        if (intentoDetalle) {
          const updated = (res.data.intentos || []).find(x => x.id === intentoDetalle.id);
          if (updated) setIntentoDetalle(updated);
        }
      }
    } catch (err) {
      alert(err.response?.data?.detail || 'Error al actualizar la nota (¿tenés permisos de admin en Capacitaciones?)');
    }
  };

  // Emitir constancia/certificado desde acá reutilizando el endpoint de Capacitaciones
  const abrirEnviarEmailModal = (intento) => {
    if (!intento?.inscripcion?.id) return;
    setEnviarForm({ tipo: 'certificado', mensaje_adicional: '' });
    setEnviarModal({
      inscripcion: intento.inscripcion,
      participante: intento.usuario,
    });
  };

  const enviarCertificadoPorEmail = async () => {
    if (!enviarModal) return;
    const cursoId = resultados?.curso_vinculado_id;
    if (!cursoId) {
      alert('No hay curso vinculado.');
      return;
    }
    setEnviandoEmail(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_URL}/capacitaciones/admin/cursos/${cursoId}/enviar-certificado-individual`,
        {
          inscripto_id: enviarModal.inscripcion.id,
          tipo: enviarForm.tipo,
          mensaje_adicional: enviarForm.mensaje_adicional,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const nom = `${enviarModal.participante?.nombre || ''} ${enviarModal.participante?.apellido || ''}`.trim();
      alert(`✅ ${enviarForm.tipo === 'certificado' ? 'Certificado' : 'Constancia'} enviado por email a ${nom || 'el participante'}.`);
      setEnviarModal(null);
    } catch (err) {
      alert('Error al enviar: ' + (err.response?.data?.detail || err.message));
    } finally {
      setEnviandoEmail(false);
    }
  };

  const emitirDesdeQuiz = async (inscripcionId, tipo) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API_URL}/capacitaciones/admin/inscriptos/${inscripcionId}/emitir/${tipo}`,
        { headers: { Authorization: `Bearer ${token}` }, responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${tipo}_${inscripcionId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      let errorMsg = 'Error al emitir documento';
      if (err.response?.data instanceof Blob) {
        try {
          const text = await err.response.data.text();
          const parsed = JSON.parse(text);
          errorMsg = parsed.detail || errorMsg;
        } catch { /* keep default */ }
      } else if (err.response?.data?.detail) {
        errorMsg = err.response.data.detail;
      }
      alert(errorMsg);
    }
  };

  const eliminarQuiz = async (quizId) => {
    if (!window.confirm('¿Eliminar este examen y todos sus intentos?')) return;
    try {
      await axios.delete(`${API_URL}/campus/espacios/${spaceId}/quizzes/${quizId}`, { headers });
      fetchQuizzes();
    } catch (err) {
      alert('Error al eliminar');
    }
  };

  // ============ VIEWS ============

  // --- CREATE/EDIT VIEW ---
  if (view === 'create') {
    return (
      <div className="space-y-4">
        <Button variant="ghost" size="sm" onClick={cancelarEdicionQuiz} data-testid="back-to-quizzes">
          <ArrowLeft className="w-4 h-4 mr-1" /> Volver
        </Button>

        <Card>
          <CardHeader><CardTitle className="text-lg">{editingQuizId ? 'Editar Examen' : 'Crear Examen'}</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <Input placeholder="Título del quiz" value={form.titulo}
              onChange={e => setForm({ ...form, titulo: e.target.value })}
              data-testid="quiz-title-input" />
            <textarea className="w-full border rounded-md px-3 py-2 text-sm min-h-[60px] resize-y"
              placeholder="Descripción (opcional)" value={form.descripcion}
              onChange={e => setForm({ ...form, descripcion: e.target.value })}
              data-testid="quiz-desc-input" />
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div>
                <label className="text-xs font-medium text-gray-600">Tiempo límite (min)</label>
                <Input type="number" min="1" placeholder="Sin límite" value={form.tiempo_limite}
                  onChange={e => setForm({ ...form, tiempo_limite: e.target.value })}
                  className="h-8 text-sm" data-testid="quiz-time-input" />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600">Intentos permitidos</label>
                <Input type="number" min="1" max="10" value={form.intentos_permitidos}
                  onChange={e => setForm({ ...form, intentos_permitidos: e.target.value })}
                  className="h-8 text-sm" data-testid="quiz-attempts-input" />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600">Correctas para aprobar</label>
                <div className="flex items-center gap-1">
                  <Input type="number" min="0" max={preguntas.length || 1} value={form.aprobacion_min_correctas}
                    placeholder={preguntas.length > 0 ? String(Math.max(1, Math.ceil(preguntas.length * 0.6))) : '—'}
                    onChange={e => setForm({ ...form, aprobacion_min_correctas: e.target.value })}
                    className="h-8 text-sm" data-testid="quiz-aprobacion-input" />
                  <span className="text-xs text-gray-500 whitespace-nowrap">/ {preguntas.length || 0}</span>
                </div>
                <p className="text-[10px] text-gray-400 mt-0.5">Vacío = 60% del total</p>
              </div>
              <div className="flex items-end">
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input type="checkbox" checked={form.mostrar_respuestas}
                    onChange={e => setForm({ ...form, mostrar_respuestas: e.target.checked })} />
                  Mostrar corrección
                </label>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Preguntas */}
        {preguntas.map((p, pIdx) => (
          <Card key={pIdx} data-testid={`pregunta-form-${pIdx}`}>
            <CardContent className="p-4 space-y-3">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <span className="text-sm font-semibold text-violet-600">Pregunta {pIdx + 1}</span>
                <div className="flex items-center gap-2">
                  <select
                    value={p.tipo || 'choice'}
                    onChange={e => {
                      const nuevoTipo = e.target.value;
                      setPreguntas(prev => prev.map((q, i) => i === pIdx
                        ? {
                            ...q,
                            tipo: nuevoTipo,
                            opciones: nuevoTipo === 'texto_abierto' ? [] : (q.opciones?.length >= 2 ? q.opciones : [
                              { texto: '', es_correcta: true },
                              { texto: '', es_correcta: false },
                            ])
                          }
                        : q
                      ));
                    }}
                    className="border rounded px-2 py-1 text-xs"
                    data-testid={`pregunta-tipo-${pIdx}`}
                  >
                    <option value="choice">Opción múltiple</option>
                    <option value="texto_abierto">Texto abierto</option>
                  </select>
                  <label className="text-xs text-gray-500">Pts:</label>
                  <Input type="number" min="1" max="100" value={p.puntos} className="w-16 h-7 text-xs"
                    onChange={e => updatePregunta(pIdx, 'puntos', parseInt(e.target.value) || 1)} />
                  <button onClick={() => removePregunta(pIdx)} className="text-red-400 hover:text-red-600">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
              <textarea className="w-full border rounded-md px-3 py-2 text-sm min-h-[50px] resize-y"
                placeholder="Texto de la pregunta..." value={p.texto}
                onChange={e => updatePregunta(pIdx, 'texto', e.target.value)}
                data-testid={`pregunta-texto-${pIdx}`} />

              {(p.tipo || 'choice') === 'choice' ? (
                <div className="space-y-2 pl-4">
                  {p.opciones.map((o, oIdx) => (
                    <div key={oIdx} className="flex items-center gap-2">
                      <button
                        onClick={() => updateOpcion(pIdx, oIdx, 'es_correcta', true)}
                        className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
                          o.es_correcta ? 'border-green-500 bg-green-500' : 'border-gray-300 hover:border-green-400'
                        }`}
                        title={o.es_correcta ? 'Respuesta correcta' : 'Marcar como correcta'}
                        data-testid={`opcion-correcta-${pIdx}-${oIdx}`}
                      >
                        {o.es_correcta && <CheckCircle2 className="w-3 h-3 text-white" />}
                      </button>
                      <Input className="flex-1 h-8 text-sm" placeholder={`Opción ${oIdx + 1}`}
                        value={o.texto}
                        onChange={e => updateOpcion(pIdx, oIdx, 'texto', e.target.value)}
                        data-testid={`opcion-texto-${pIdx}-${oIdx}`} />
                      {p.opciones.length > 2 && (
                        <button onClick={() => removeOpcion(pIdx, oIdx)} className="text-gray-300 hover:text-red-400">
                          <X className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  ))}
                  <Button variant="ghost" size="sm" onClick={() => addOpcion(pIdx)} className="text-xs text-gray-500">
                    <Plus className="w-3 h-3 mr-1" /> Agregar opción
                  </Button>
                </div>
              ) : (
                <div className="pl-4 text-xs text-gray-500 italic border-l-2 border-violet-200 py-1">
                  El alumno responderá con un texto libre. Vos corregís manualmente en la bandeja de corrección.
                </div>
              )}
            </CardContent>
          </Card>
        ))}

        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={() => addPregunta('choice')} className="flex-1 border-dashed" data-testid="add-question-btn">
            <Plus className="w-4 h-4 mr-1" /> Pregunta de opción múltiple
          </Button>
          <Button variant="outline" onClick={() => addPregunta('texto_abierto')} className="flex-1 border-dashed" data-testid="add-question-text-btn">
            <Plus className="w-4 h-4 mr-1" /> Pregunta de texto abierto
          </Button>
        </div>

        {preguntas.length > 0 && (
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={cancelarEdicionQuiz}>Cancelar</Button>
            <Button onClick={guardarQuiz} disabled={saving} data-testid="save-quiz-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
              {editingQuizId
                ? `Guardar cambios (${preguntas.length} pregunta${preguntas.length !== 1 ? 's' : ''})`
                : `Crear Examen (${preguntas.length} pregunta${preguntas.length !== 1 ? 's' : ''})`}
            </Button>
          </div>
        )}
      </div>
    );
  }

  // --- TAKE QUIZ VIEW ---
  if (view === 'take' && selectedQuiz) {
    const quiz = selectedQuiz;
    const preguntasList = quiz.preguntas || [];

    if (resultado) {
      const tienePendientes = !!resultado.tiene_pendientes;
      // Show results
      return (
        <div className="space-y-4">
          <Button variant="ghost" size="sm" onClick={() => { setView('list'); setResultado(null); setSelectedQuiz(null); }} data-testid="back-after-quiz">
            <ArrowLeft className="w-4 h-4 mr-1" /> Volver a exámenes
          </Button>

          {tienePendientes ? (
            <Card className="text-center border-amber-300 bg-amber-50" data-testid="quiz-result-card">
              <CardContent className="py-8 space-y-2">
                <Timer className="w-10 h-10 text-amber-500 mx-auto" />
                <h3 className="font-bold text-lg text-amber-900">Examen enviado</h3>
                <p className="text-sm text-amber-800 max-w-md mx-auto">
                  {resultado.mensaje || 'La nota final aparecerá cuando el docente termine de corregir las preguntas abiertas.'}
                </p>
                <p className="text-xs text-gray-500">Intento #{resultado.intento_numero}</p>
              </CardContent>
            </Card>
          ) : (() => {
            const totalP = resultado.total_preguntas ?? (selectedQuiz?.preguntas?.length || 0);
            const correctas = resultado.preguntas_correctas ?? 0;
            const minAprob = resultado.aprobacion_min_correctas ?? Math.max(1, Math.ceil(totalP * 0.6));
            const aprobado = resultado.aprobado === true || (resultado.aprobado === undefined && correctas >= minAprob);
            return (
              <Card className="text-center" data-testid="quiz-result-card">
                <CardContent className="py-8">
                  <div className={`text-5xl font-bold mb-2 ${aprobado ? 'text-green-600' : 'text-red-500'}`}>
                    {correctas}<span className="text-2xl text-gray-400"> / {totalP}</span>
                  </div>
                  <p className="text-gray-600">{resultado.puntaje} / {resultado.puntaje_maximo} puntos · {resultado.porcentaje}%</p>
                  <p className="text-sm text-gray-400 mt-1">Intento #{resultado.intento_numero}</p>
                  <Badge className={`mt-3 ${aprobado ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'} border-0`}>
                    {aprobado ? 'Aprobado' : 'Desaprobado'} (mínimo {minAprob} correctas)
                  </Badge>
                </CardContent>
              </Card>
            );
          })()}

          {/* Correction detail (solo si no hay pendientes) */}
          {!tienePendientes && resultado.detalle && (
            <div className="space-y-3">
              <h3 className="font-semibold text-gray-700">Corrección</h3>
              {preguntasList.map((p, idx) => {
                const detalle = resultado.detalle?.find(d => d.pregunta_id === p.id);
                const correccion = resultado.correcciones?.find(c => c.pregunta_id === p.id);
                const esCorrecta = detalle?.es_correcta;
                if ((p.tipo || 'choice') === 'texto_abierto') {
                  return (
                    <Card key={p.id || idx} className="border-l-4 border-l-violet-400" data-testid={`correccion-${idx}`}>
                      <CardContent className="p-4">
                        <p className="text-sm font-medium">{p.texto}</p>
                        <div className="mt-2 text-sm bg-gray-50 rounded p-2 whitespace-pre-wrap">
                          {detalle?.texto || <em className="text-gray-400">Sin respuesta</em>}
                        </div>
                        {detalle?.corregida && (
                          <div className="mt-2 text-xs text-gray-600">
                            Puntaje: <strong>{detalle.puntos_obtenidos}</strong> / {p.puntos}
                            {detalle.feedback && <p className="mt-1 italic">Feedback: {detalle.feedback}</p>}
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  );
                }
                return (
                  <Card key={p.id || idx} className={`border-l-4 ${esCorrecta ? 'border-l-green-500' : 'border-l-red-500'}`}
                    data-testid={`correccion-${idx}`}>
                    <CardContent className="p-4">
                      <div className="flex items-start gap-2">
                        {esCorrecta
                          ? <CheckCircle2 className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                          : <XCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />}
                        <div className="flex-1">
                          <p className="text-sm font-medium">{p.texto}</p>
                          <div className="mt-2 space-y-1">
                            {p.opciones?.map(o => {
                              const fueSeleccionada = detalle?.opcion_id === o.id;
                              const esLaCorrecta = correccion?.correcta_ids?.includes(o.id);
                              return (
                                <div key={o.id} className={`text-sm px-3 py-1.5 rounded flex items-center gap-2 ${
                                  esLaCorrecta ? 'bg-green-50 text-green-700 font-medium' :
                                  fueSeleccionada && !esCorrecta ? 'bg-red-50 text-red-600' :
                                  'text-gray-500'
                                }`}>
                                  {esLaCorrecta ? <CheckCircle2 className="w-3.5 h-3.5" /> :
                                   fueSeleccionada ? <XCircle className="w-3.5 h-3.5" /> :
                                   <CircleDot className="w-3.5 h-3.5 text-gray-300" />}
                                  {o.texto}
                                  {fueSeleccionada && <span className="text-xs ml-auto">(tu respuesta)</span>}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </div>
      );
    }

    // Quiz taking UI
    return (
      <div className="space-y-4">
        <Button variant="ghost" size="sm" onClick={() => { setView('list'); setSelectedQuiz(null); }} data-testid="cancel-quiz">
          <ArrowLeft className="w-4 h-4 mr-1" /> Cancelar
        </Button>

        <Card>
          <CardContent className="p-5">
            <h2 className="text-xl font-bold" data-testid="quiz-take-title">{quiz.titulo}</h2>
            {quiz.descripcion && <p className="text-sm text-gray-500 mt-1">{quiz.descripcion}</p>}
            <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
              <span>{preguntasList.length} preguntas</span>
              <span>{quiz.puntaje_total} puntos</span>
              {quiz.tiempo_limite && (
                <span className="flex items-center gap-1"><Timer className="w-3 h-3" />{quiz.tiempo_limite} min</span>
              )}
            </div>
          </CardContent>
        </Card>

        {preguntasList.map((p, idx) => {
          const tipo = p.tipo || 'choice';
          const respVal = respuestas[p.id];
          return (
          <Card key={p.id || idx} data-testid={`quiz-pregunta-${idx}`}>
            <CardContent className="p-4">
              <div className="flex items-start justify-between mb-3">
                <p className="text-sm font-medium flex-1">
                  <span className="text-violet-600 mr-2">{idx + 1}.</span>
                  {p.texto}
                </p>
                <span className="text-xs text-gray-400 ml-2">{p.puntos} pt{p.puntos !== 1 ? 's' : ''}</span>
              </div>
              {tipo === 'texto_abierto' ? (
                <textarea
                  className="w-full border rounded-md px-3 py-2 text-sm min-h-[100px] resize-y"
                  placeholder="Escribí tu respuesta..."
                  value={typeof respVal === 'string' && respVal.startsWith('TXT:') ? respVal.slice(4) : ''}
                  onChange={e => setRespuestas(prev => {
                    const v = e.target.value;
                    if (!v) {
                      const n = { ...prev };
                      delete n[p.id];
                      return n;
                    }
                    return { ...prev, [p.id]: 'TXT:' + v };
                  })}
                  data-testid={`quiz-text-${p.id}`}
                />
              ) : (
                <div className="space-y-2 pl-4">
                  {p.opciones?.map(o => (
                    <label key={o.id}
                      className={`flex items-center gap-3 p-2.5 rounded-lg border cursor-pointer transition-colors ${
                        respVal === o.id
                          ? 'border-violet-400 bg-violet-50'
                          : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                      }`}
                      data-testid={`quiz-opcion-${p.id}-${o.id}`}
                    >
                      <input type="radio" name={`q-${p.id}`}
                        checked={respVal === o.id}
                        onChange={() => setRespuestas(prev => ({ ...prev, [p.id]: o.id }))}
                        className="accent-violet-600" />
                      <span className="text-sm">{o.texto}</span>
                    </label>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
          );
        })}

        <div className="flex justify-between items-center">
          <span className="text-sm text-gray-500">
            {Object.keys(respuestas).length} / {preguntasList.length} respondidas
          </span>
          <Button onClick={enviarRespuestas} disabled={submittingQuiz || Object.keys(respuestas).length === 0}
            data-testid="submit-quiz-btn">
            {submittingQuiz ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <CheckCircle2 className="w-4 h-4 mr-1" />}
            Enviar Respuestas
          </Button>
        </div>
      </div>
    );
  }

  // Modal reusable de envío por email (se puede montar desde list-view o results-view)
  const enviarEmailModalJSX = enviarModal ? (
    <div
      className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4"
      onClick={() => !enviandoEmail && setEnviarModal(null)}
      data-testid="enviar-email-modal"
    >
      <div
        className="bg-white rounded-lg shadow-xl w-full max-w-md flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-4 py-3 border-b">
          <div className="flex items-center gap-2">
            <Mail className="w-5 h-5 text-blue-600" />
            <h3 className="font-semibold">Enviar por email</h3>
          </div>
          <button onClick={() => setEnviarModal(null)} disabled={enviandoEmail} className="text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-4 space-y-3">
          <p className="text-sm text-gray-700">
            Se enviará a <strong>{enviarModal.participante?.nombre} {enviarModal.participante?.apellido}</strong>
            {enviarModal.participante?.email && (
              <> (<span className="text-gray-500">{enviarModal.participante.email}</span>)</>
            )}
          </p>

          <div>
            <label className="text-xs font-medium text-gray-600 uppercase mb-1 block">Tipo de documento</label>
            <div className="flex gap-2">
              <label className={`flex-1 border rounded-md px-3 py-2 text-sm cursor-pointer flex items-center gap-2 ${
                enviarForm.tipo === 'constancia' ? 'border-violet-500 bg-violet-50' : 'border-gray-200'
              }`}>
                <input type="radio" name="tipo" value="constancia"
                  checked={enviarForm.tipo === 'constancia'}
                  onChange={() => setEnviarForm(f => ({ ...f, tipo: 'constancia' }))}
                  data-testid="tipo-constancia" />
                <FileText className="w-4 h-4 text-violet-600" /> Constancia
              </label>
              <label className={`flex-1 border rounded-md px-3 py-2 text-sm cursor-pointer flex items-center gap-2 ${
                enviarForm.tipo === 'certificado' ? 'border-amber-500 bg-amber-50' : 'border-gray-200'
              }`}>
                <input type="radio" name="tipo" value="certificado"
                  checked={enviarForm.tipo === 'certificado'}
                  onChange={() => setEnviarForm(f => ({ ...f, tipo: 'certificado' }))}
                  data-testid="tipo-certificado" />
                <Award className="w-4 h-4 text-amber-600" /> Certificado
              </label>
            </div>
          </div>

          <div>
            <label className="text-xs font-medium text-gray-600 uppercase mb-1 block">Mensaje adicional (opcional)</label>
            <textarea
              value={enviarForm.mensaje_adicional}
              onChange={(e) => setEnviarForm(f => ({ ...f, mensaje_adicional: e.target.value }))}
              rows={3}
              placeholder="Se agrega al cuerpo del email"
              className="w-full border border-gray-300 rounded p-2 text-sm"
              data-testid="mensaje-adicional"
            />
          </div>
        </div>
        <div className="flex justify-end gap-2 p-3 border-t bg-gray-50">
          <Button variant="outline" size="sm" onClick={() => setEnviarModal(null)} disabled={enviandoEmail}>
            Cancelar
          </Button>
          <Button size="sm" onClick={enviarCertificadoPorEmail} disabled={enviandoEmail} data-testid="enviar-confirmar">
            {enviandoEmail ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Send className="w-4 h-4 mr-1" />}
            Enviar
          </Button>
        </div>
      </div>
    </div>
  ) : null;

  // --- RESULTS VIEW ---
  if (view === 'results' && selectedQuiz) {
    // Detalle de un intento: lista de preguntas + respuestas del alumno
    if (intentoDetalle) {
      const preguntas = resultados?.quiz?.preguntas || selectedQuiz?.preguntas || [];
      const respMap = {};
      (intentoDetalle.respuestas || []).forEach(r => { respMap[r.pregunta_id] = r; });
      const totalP = intentoDetalle.total_preguntas ?? preguntas.length;
      const correctas = intentoDetalle.preguntas_correctas ?? 0;
      const minAprob = intentoDetalle.aprobacion_min_correctas ?? resultados.estadisticas?.aprobacion_min_correctas;
      const aprobado = intentoDetalle.aprobado === true
        || (intentoDetalle.aprobado === undefined && minAprob !== undefined && correctas >= minAprob);

      return (
        <div className="space-y-4">
          <Button variant="ghost" size="sm" onClick={() => setIntentoDetalle(null)} data-testid="back-from-detalle">
            <ArrowLeft className="w-4 h-4 mr-1" /> Volver a resultados
          </Button>

          <Card>
            <CardContent className="p-4 flex flex-wrap items-center justify-between gap-3">
              <div>
                <h2 className="text-lg font-bold">{selectedQuiz.titulo}</h2>
                <p className="text-sm text-gray-600">
                  Intento de <strong>{intentoDetalle.usuario?.nombre} {intentoDetalle.usuario?.apellido}</strong>
                  {intentoDetalle.usuario?.email && <span className="text-gray-400"> · {intentoDetalle.usuario.email}</span>}
                </p>
                <p className="text-xs text-gray-400 mt-1">
                  Intento #{intentoDetalle.intento_numero}
                  {intentoDetalle.fecha_fin && ` · ${new Date(intentoDetalle.fecha_fin).toLocaleString('es-AR')}`}
                </p>
              </div>
              <div className="text-right">
                <div className={`text-2xl font-bold ${aprobado ? 'text-green-600' : intentoDetalle.tiene_pendientes ? 'text-amber-600' : 'text-red-500'}`}>
                  {correctas}/{totalP}
                </div>
                <div className="text-xs text-gray-500">
                  {intentoDetalle.puntaje}/{intentoDetalle.puntaje_maximo} pts · {intentoDetalle.porcentaje}%
                </div>
                <Badge variant="outline" className={`mt-1 ${aprobado ? 'text-green-700 border-green-300' : intentoDetalle.tiene_pendientes ? 'text-amber-700 border-amber-300' : 'text-red-700 border-red-300'}`}>
                  {intentoDetalle.tiene_pendientes ? 'Pendiente corrección' : (aprobado ? 'Aprobado' : 'No aprobado')}
                </Badge>
              </div>
            </CardContent>
          </Card>

          {preguntas.length === 0 ? (
            <Card><CardContent className="p-4 text-sm text-gray-500">No se encontraron las preguntas del examen.</CardContent></Card>
          ) : (
            preguntas.map((preg, idx) => {
              const resp = respMap[preg.id];
              const opciones = preg.opciones || [];
              const isChoice = (preg.tipo || 'choice') !== 'texto_abierto';
              const correctOptionIds = new Set(opciones.filter(o => o.es_correcta).map(o => o.id));
              const elegidoId = resp?.opcion_id;
              return (
                <Card key={preg.id} data-testid={`detalle-pregunta-${idx}`}>
                  <CardContent className="p-4 space-y-3">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className="text-xs text-gray-500 uppercase font-semibold">
                          Pregunta {idx + 1} · {preg.puntos || 1} pt{(preg.puntos || 1) !== 1 ? 's' : ''}
                          {isChoice ? '' : ' · Respuesta abierta'}
                        </p>
                        <p className="text-sm font-medium mt-1 whitespace-pre-wrap">{preg.texto}</p>
                      </div>
                      {resp && (
                        resp.es_correcta === true ? (
                          <Badge variant="outline" className="text-green-700 border-green-300 flex-shrink-0">
                            <CheckCircle2 className="w-3 h-3 mr-1" /> Correcta
                          </Badge>
                        ) : resp.es_correcta === false ? (
                          <Badge variant="outline" className="text-red-700 border-red-300 flex-shrink-0">
                            <XCircle className="w-3 h-3 mr-1" /> Incorrecta
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="text-amber-700 border-amber-300 flex-shrink-0">
                            <Clock className="w-3 h-3 mr-1" /> Pendiente
                          </Badge>
                        )
                      )}
                    </div>

                    {isChoice ? (
                      <div className="space-y-1.5">
                        {opciones.map(op => {
                          const fueElegida = op.id === elegidoId;
                          const esCorrecta = correctOptionIds.has(op.id);
                          let cls = 'border-gray-200 bg-white text-gray-700';
                          if (esCorrecta && fueElegida) cls = 'border-green-400 bg-green-50 text-green-800';
                          else if (esCorrecta) cls = 'border-green-300 bg-green-50/40 text-green-700';
                          else if (fueElegida) cls = 'border-red-400 bg-red-50 text-red-800';
                          return (
                            <div key={op.id}
                              className={`flex items-center gap-2 px-3 py-2 border rounded-md text-sm ${cls}`}
                              data-testid={`detalle-opcion-${idx}-${op.id}`}>
                              <span className="w-4 h-4 rounded-full border-2 flex items-center justify-center flex-shrink-0">
                                {fueElegida && <span className="w-2 h-2 rounded-full bg-current" />}
                              </span>
                              <span className="flex-1">{op.texto}</span>
                              {esCorrecta && <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0" />}
                              {fueElegida && !esCorrecta && <XCircle className="w-4 h-4 text-red-500 flex-shrink-0" />}
                              {fueElegida && (
                                <span className="text-[10px] uppercase text-gray-500 ml-1">Eligió</span>
                              )}
                            </div>
                          );
                        })}
                        {!resp && (
                          <p className="text-xs text-gray-400 italic">El alumno no respondió esta pregunta.</p>
                        )}
                      </div>
                    ) : (
                      <div className="bg-gray-50 rounded-md p-3">
                        <p className="text-xs text-gray-500 mb-1">Respuesta del alumno:</p>
                        <p className="text-sm whitespace-pre-wrap">
                          {resp?.texto || <em className="text-gray-400">Sin respuesta</em>}
                        </p>
                        {resp?.corregida && (
                          <p className="text-xs text-gray-500 mt-2">
                            Puntaje: <strong>{resp.puntos_obtenidos}/{preg.puntos || 1}</strong>
                          </p>
                        )}
                        {resp?.feedback && (
                          <div className="mt-2 pt-2 border-t border-gray-200">
                            <p className="text-xs text-gray-500 mb-0.5">Feedback del docente:</p>
                            <p className="text-sm text-gray-700 whitespace-pre-wrap">{resp.feedback}</p>
                          </div>
                        )}
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })
          )}
        </div>
      );
    }

    return (
      <div className="space-y-4">
        <Button variant="ghost" size="sm" onClick={() => { setView('list'); setResultados(null); setSelectedQuiz(null); setIntentoDetalle(null); }}
          data-testid="back-from-results">
          <ArrowLeft className="w-4 h-4 mr-1" /> Volver
        </Button>

        <h2 className="text-lg font-bold">Resultados: {selectedQuiz.titulo}</h2>

        {resultados && !resultados.curso_vinculado_id && esCoordinadorODocente && (
          <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2"
            data-testid="no-curso-vinculado-hint">
            💡 Para emitir constancias/certificados desde acá, vinculá este espacio a un curso de Capacitaciones
            (editá el espacio y setéa <strong>Curso vinculado</strong>).
          </div>
        )}

        {loadingResults ? (
          <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-violet-600" /></div>
        ) : (
          <>
            {resultados?.estadisticas && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="quiz-stats">
                <Card><CardContent className="pt-3 pb-2 text-center">
                  <div className="text-2xl font-bold text-violet-600">{resultados.estadisticas.total_intentos}</div>
                  <div className="text-xs text-gray-500">Intentos</div>
                </CardContent></Card>
                <Card><CardContent className="pt-3 pb-2 text-center">
                  <div className="text-2xl font-bold text-blue-600">{resultados.estadisticas.promedio}%</div>
                  <div className="text-xs text-gray-500">Promedio</div>
                </CardContent></Card>
                <Card><CardContent className="pt-3 pb-2 text-center">
                  <div className="text-2xl font-bold text-green-600">{resultados.estadisticas.aprobados}</div>
                  <div className="text-xs text-gray-500">Aprobados</div>
                </CardContent></Card>
                <Card><CardContent className="pt-3 pb-2 text-center">
                  <div className="text-2xl font-bold text-gray-600">{resultados.estadisticas.usuarios_unicos}</div>
                  <div className="text-xs text-gray-500">Participantes</div>
                </CardContent></Card>
              </div>
            )}

            <Card>
              <CardContent className="p-0">
                {resultados?.intentos?.length === 0 ? (
                  <div className="p-6 text-center text-sm text-gray-500">Sin intentos registrados</div>
                ) : (
                  <div className="divide-y">
                    {resultados?.intentos?.map(i => {
                      const totalP = i.total_preguntas ?? (selectedQuiz?.preguntas?.length || 0);
                      const correctas = i.preguntas_correctas ?? null;
                      const minAprob = i.aprobacion_min_correctas ?? resultados.estadisticas?.aprobacion_min_correctas;
                      const aprobado = i.aprobado === true || (i.aprobado === undefined && correctas !== null && minAprob !== undefined && correctas >= minAprob);
                      return (
                      <div key={i.id}
                        className="flex items-center justify-between px-4 py-3 hover:bg-violet-50/40 transition-colors gap-3"
                        data-testid={`result-row-${i.id}`}>
                        {/* Datos del participante — clickeable para ver detalle */}
                        <button type="button"
                          onClick={() => setIntentoDetalle(i)}
                          className="flex items-center gap-3 text-left flex-1 min-w-0"
                          data-testid={`result-${i.id}`}
                          title="Ver respuestas del alumno">
                          <div className="w-8 h-8 bg-violet-100 rounded-full flex items-center justify-center text-violet-600 font-semibold text-xs">
                            {(i.usuario?.nombre?.[0] || '?').toUpperCase()}
                          </div>
                          <div className="min-w-0">
                            <span className="text-sm font-medium block truncate">
                              {i.usuario?.nombre} {i.usuario?.apellido}
                            </span>
                            <p className="text-xs text-gray-400">
                              Intento #{i.intento_numero}
                              {i.tiene_pendientes && <span className="ml-2 text-amber-600">· pendiente corrección</span>}
                            </p>
                          </div>
                        </button>

                        {/* Puntaje */}
                        <div className="text-right w-24 flex-shrink-0">
                          <div className={`text-lg font-bold ${aprobado ? 'text-green-600' : i.tiene_pendientes ? 'text-amber-600' : 'text-red-500'}`}>
                            {correctas !== null ? `${correctas}/${totalP}` : `${i.porcentaje}%`}
                          </div>
                          <div className="text-xs text-gray-400">{i.puntaje}/{i.puntaje_maximo} pts</div>
                        </div>

                        {/* Acredita + Nota + Emitir (solo si el espacio está vinculado a un curso) */}
                        {resultados?.curso_vinculado_id && i.inscripcion && (
                          <div className="flex items-center gap-2 flex-shrink-0" data-testid={`insc-actions-${i.id}`}>
                            {/* Acredita */}
                            <div className="w-20 text-center">
                              {i.inscripcion.acredita === true ? (
                                <span className="inline-flex items-center gap-1 text-xs text-green-700 font-medium">
                                  <CheckCircle2 className="w-3.5 h-3.5" /> Sí
                                </span>
                              ) : i.inscripcion.cancelada ? (
                                <span className="text-xs text-gray-400">Cancelada</span>
                              ) : (
                                <span className="inline-flex items-center gap-1 text-xs text-gray-500">
                                  <XCircle className="w-3.5 h-3.5" /> No
                                </span>
                              )}
                            </div>

                            {/* Nota (editable con selector Aprobado / Desaprobado / Numérico) */}
                            <div className="w-32">
                              <select
                                value={
                                  i.inscripcion.estado_aprobacion === 'aprobado' ? 'aprobado'
                                  : i.inscripcion.estado_aprobacion === 'desaprobado' ? 'desaprobado'
                                  : (i.inscripcion.nota !== null && i.inscripcion.nota !== undefined) ? 'numero'
                                  : 'ninguno'
                                }
                                onChange={async (e) => {
                                  const v = e.target.value;
                                  if (v === 'aprobado' || v === 'desaprobado') {
                                    await actualizarNotaInscripcion(i.inscripcion.id, { estado_aprobacion: v, nota: null });
                                  } else if (v === 'ninguno') {
                                    await actualizarNotaInscripcion(i.inscripcion.id, { nota: null, estado_aprobacion: null });
                                  } else if (v === 'numero') {
                                    const raw = window.prompt('Ingresá la nota (0-10):', i.inscripcion.nota ?? '');
                                    if (raw === null) return;
                                    const parsed = parseFloat(raw);
                                    if (isNaN(parsed)) { alert('Nota inválida'); return; }
                                    await actualizarNotaInscripcion(i.inscripcion.id, { nota: parsed, estado_aprobacion: null });
                                  }
                                }}
                                className="h-8 border border-gray-300 rounded text-xs px-1 w-full"
                                data-testid={`nota-select-${i.id}`}
                                onClick={(e) => e.stopPropagation()}
                              >
                                <option value="ninguno">Sin calificar</option>
                                <option value="numero">
                                  {i.inscripcion.nota !== null && i.inscripcion.nota !== undefined
                                    ? `Nota: ${i.inscripcion.nota}`
                                    : 'Nota numérica…'}
                                </option>
                                <option value="aprobado">Aprobado</option>
                                <option value="desaprobado">Desaprobado</option>
                              </select>
                            </div>

                            {/* Emitir constancia */}
                            <button
                              type="button"
                              onClick={() => emitirDesdeQuiz(i.inscripcion.id, 'constancia')}
                              className="p-1.5 text-gray-500 hover:text-violet-600 hover:bg-violet-50 rounded"
                              title="Emitir constancia"
                              data-testid={`emitir-constancia-${i.id}`}
                            >
                              <FileText className="w-4 h-4" />
                            </button>

                            {/* Emitir certificado */}
                            <button
                              type="button"
                              onClick={() => emitirDesdeQuiz(i.inscripcion.id, 'certificado')}
                              className="p-1.5 text-gray-500 hover:text-amber-600 hover:bg-amber-50 rounded"
                              title="Emitir certificado"
                              data-testid={`emitir-certificado-${i.id}`}
                            >
                              <Award className="w-4 h-4" />
                            </button>

                            {/* Enviar por email */}
                            <button
                              type="button"
                              onClick={() => abrirEnviarEmailModal(i)}
                              disabled={!i.usuario?.email || !i.inscripcion?.asistio}
                              className="p-1.5 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded disabled:opacity-40 disabled:cursor-not-allowed"
                              title={
                                !i.usuario?.email ? 'Sin email del participante'
                                : !i.inscripcion?.asistio ? 'El participante debe estar como asistido para enviar por email'
                                : 'Enviar constancia/certificado por email'
                              }
                              data-testid={`enviar-email-${i.id}`}
                            >
                              <Send className="w-4 h-4" />
                            </button>
                          </div>
                        )}

                        {/* Indicador de detalle */}
                        <button type="button"
                          onClick={() => setIntentoDetalle(i)}
                          className="text-violet-400 hover:text-violet-600 flex-shrink-0"
                          title="Ver detalle"
                          aria-label="Ver detalle">
                          <FileText className="w-4 h-4" />
                        </button>
                      </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </>
        )}
        {enviarEmailModalJSX}
      </div>
    );
  }

  // --- CORRECTION VIEW (docente) ---
  if (view === 'correct' && selectedQuiz) {
    const quiz = pendientes?.quiz || selectedQuiz;
    const intentos = pendientes?.intentos || [];
    const preguntasMap = {};
    (quiz.preguntas || []).forEach(p => { preguntasMap[p.id] = p; });

    if (intentoEnCorreccion) {
      const intento = intentoEnCorreccion;
      const respuestasAbiertas = (intento.respuestas || []).filter(r => r.tipo === 'texto_abierto');
      return (
        <div className="space-y-4">
          <Button variant="ghost" size="sm" onClick={() => { setIntentoEnCorreccion(null); setCorreccionDraft({}); }}>
            <ArrowLeft className="w-4 h-4 mr-1" /> Volver a pendientes
          </Button>
          <Card>
            <CardContent className="p-4">
              <h2 className="text-lg font-bold">{quiz.titulo}</h2>
              <p className="text-sm text-gray-600">
                Corrigiendo a: <strong>{intento.usuario?.nombre} {intento.usuario?.apellido}</strong>
              </p>
            </CardContent>
          </Card>
          {respuestasAbiertas.map((r, idx) => {
            const pregunta = preguntasMap[r.pregunta_id];
            const draft = correccionDraft[r.pregunta_id] || { puntos_obtenidos: 0, feedback: '', max: pregunta?.puntos || 1 };
            const maxPts = pregunta?.puntos || 1;
            return (
              <Card key={r.pregunta_id} data-testid={`corregir-pregunta-${idx}`}>
                <CardContent className="p-4 space-y-3">
                  <div>
                    <p className="text-xs text-gray-500 uppercase font-semibold">Pregunta {idx + 1} · {maxPts} pt{maxPts !== 1 ? 's' : ''}</p>
                    <p className="text-sm font-medium mt-1">{pregunta?.texto || '(pregunta no encontrada)'}</p>
                  </div>
                  <div className="bg-gray-50 rounded p-3">
                    <p className="text-xs text-gray-500 mb-1">Respuesta del alumno:</p>
                    <p className="text-sm whitespace-pre-wrap">{r.texto || <em className="text-gray-400">Sin respuesta</em>}</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <label className="text-sm font-medium">Puntaje:</label>
                    <Input
                      type="number" min="0" max={maxPts}
                      value={draft.puntos_obtenidos}
                      onChange={e => setCorreccionDraft(d => ({
                        ...d,
                        [r.pregunta_id]: { ...draft, puntos_obtenidos: e.target.value }
                      }))}
                      className="w-20 h-8"
                      data-testid={`corr-pts-${idx}`}
                    />
                    <span className="text-sm text-gray-500">/ {maxPts}</span>
                  </div>
                  <textarea
                    placeholder="Feedback opcional para el alumno..."
                    className="w-full border rounded-md px-3 py-2 text-sm min-h-[60px] resize-y"
                    value={draft.feedback}
                    onChange={e => setCorreccionDraft(d => ({
                      ...d,
                      [r.pregunta_id]: { ...draft, feedback: e.target.value }
                    }))}
                    data-testid={`corr-feedback-${idx}`}
                  />
                </CardContent>
              </Card>
            );
          })}
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => { setIntentoEnCorreccion(null); setCorreccionDraft({}); }}>Cancelar</Button>
            <Button onClick={guardarCorreccion} disabled={savingCorr} data-testid="save-correction-btn">
              {savingCorr ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <CheckCircle2 className="w-4 h-4 mr-1" />}
              Guardar corrección
            </Button>
          </div>
        </div>
      );
    }

    return (
      <div className="space-y-4">
        <Button variant="ghost" size="sm" onClick={() => { setView('list'); setPendientes(null); setSelectedQuiz(null); }}>
          <ArrowLeft className="w-4 h-4 mr-1" /> Volver a exámenes
        </Button>
        <Card>
          <CardContent className="p-4">
            <h2 className="text-lg font-bold flex items-center gap-2">
              <FileText className="w-5 h-5 text-amber-600" /> Bandeja de corrección
            </h2>
            <p className="text-sm text-gray-600">{quiz.titulo}</p>
          </CardContent>
        </Card>
        {loadingPendientes ? (
          <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-violet-600" /></div>
        ) : intentos.length === 0 ? (
          <Card className="text-center py-8">
            <CardContent>
              <CheckCircle2 className="w-12 h-12 text-emerald-300 mx-auto mb-3" />
              <p className="text-gray-500">No hay intentos pendientes de corrección</p>
            </CardContent>
          </Card>
        ) : (
          intentos.map(intento => {
            const pendientesIntento = (intento.respuestas || []).filter(r => r.tipo === 'texto_abierto' && !r.corregida).length;
            return (
              <Card key={intento.id} className="hover:shadow-md transition-shadow" data-testid={`pending-intento-${intento.id}`}>
                <CardContent className="p-4 flex items-center justify-between">
                  <div>
                    <p className="font-medium">{intento.usuario?.nombre} {intento.usuario?.apellido}</p>
                    <p className="text-xs text-gray-500">
                      Intento #{intento.intento_numero} · Enviado {new Date(intento.fecha_fin).toLocaleString('es-AR', { timeZone: 'America/Argentina/Buenos_Aires' })}
                    </p>
                    <Badge className="bg-amber-100 text-amber-700 border-0 mt-1 text-xs">
                      {pendientesIntento} pregunta{pendientesIntento !== 1 ? 's' : ''} por corregir
                    </Badge>
                  </div>
                  <Button size="sm" onClick={() => iniciarCorreccionIntento(intento)} data-testid={`corregir-btn-${intento.id}`}>
                    <Edit3 className="w-3.5 h-3.5 mr-1" /> Corregir
                  </Button>
                </CardContent>
              </Card>
            );
          })
        )}
      </div>
    );
  }

  // --- LIST VIEW ---
  return (
    <div className="space-y-4">
      {esCoordinadorODocente && (
        <div className="flex justify-end">
          <Button size="sm" onClick={() => { setView('create'); addPregunta(); }} data-testid="create-quiz-btn">
            <Plus className="w-4 h-4 mr-1" /> Nuevo Examen
          </Button>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-violet-600" /></div>
      ) : quizzes.length === 0 ? (
        <Card className="text-center py-8">
          <CardContent>
            <HelpCircle className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500">No hay exámenes en este espacio</p>
          </CardContent>
        </Card>
      ) : (
        quizzes.map(quiz => {
          // Bloqueo por acreditación del curso vinculado (staff pasa libre)
          const bloqueadoPorAcreditacion = !esCoordinadorODocente && quiz.puede_responder === false;
          const puedeResponder = !esCoordinadorODocente && quiz.estado === 'publicada' &&
            quiz.mis_intentos < (quiz.intentos_permitidos || 1) && !bloqueadoPorAcreditacion;
          return (
            <Card key={quiz.id} className="hover:shadow-md transition-shadow" data-testid={`quiz-card-${quiz.id}`}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <Badge variant="outline" className="text-xs">{quiz.total_preguntas} preguntas</Badge>
                      {quiz.estado === 'publicada'
                        ? <Badge className="bg-emerald-100 text-emerald-700 border-0 text-xs">Activo</Badge>
                        : quiz.estado === 'borrador'
                        ? <Badge className="bg-gray-100 text-gray-600 border-0 text-xs">Borrador</Badge>
                        : <Badge className="bg-red-100 text-red-600 border-0 text-xs">Cerrado</Badge>
                      }
                      {quiz.mi_mejor_puntaje && (() => {
                        // Para quizzes nuevos, mi_mejor_puntaje puede traer preguntas_correctas; sino usar porcentaje legacy
                        const minAprob = quiz.aprobacion_min_correctas;
                        const correctas = quiz.mi_mejor_puntaje.preguntas_correctas;
                        let aprobado;
                        if (typeof correctas === 'number' && typeof minAprob === 'number') {
                          aprobado = correctas >= minAprob;
                        } else {
                          aprobado = quiz.mi_mejor_puntaje.porcentaje >= 60;
                        }
                        return (
                          <Badge className={`border-0 text-xs ${
                            aprobado
                              ? 'bg-green-100 text-green-700'
                              : 'bg-red-100 text-red-700'
                          }`}>
                            Mejor: {typeof correctas === 'number' ? `${correctas} correctas` : `${quiz.mi_mejor_puntaje.porcentaje}%`}
                          </Badge>
                        );
                      })()}
                    </div>
                    <h3 className="font-semibold text-gray-900">{quiz.titulo}</h3>
                    {quiz.descripcion && (
                      <p className="text-sm text-gray-500 line-clamp-1 mt-1">{quiz.descripcion}</p>
                    )}
                    <div className="flex items-center gap-4 mt-2 text-xs text-gray-400">
                      <span>{quiz.puntaje_total} pts</span>
                      {quiz.tiempo_limite && (
                        <span className="flex items-center gap-1"><Timer className="w-3 h-3" />{quiz.tiempo_limite} min</span>
                      )}
                      <span>{quiz.intentos_permitidos || 1} intento{(quiz.intentos_permitidos || 1) !== 1 ? 's' : ''}</span>
                      {quiz.mis_intentos > 0 && <span>Usado: {quiz.mis_intentos}/{quiz.intentos_permitidos || 1}</span>}
                    </div>
                    {bloqueadoPorAcreditacion && (
                      <div className="mt-2 flex items-start gap-1.5 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-2.5 py-1.5"
                        data-testid={`quiz-bloqueado-${quiz.id}`}>
                        <Lock className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                        <span>{quiz.motivo_bloqueo || 'Requiere acreditar la asistencia mínima del curso.'}</span>
                      </div>
                    )}
                  </div>

                  <div className="flex items-center gap-1.5 ml-3">
                    {puedeResponder && (
                      <Button size="sm" onClick={() => iniciarQuiz(quiz)} data-testid={`start-quiz-${quiz.id}`}>
                        Responder
                      </Button>
                    )}
                    {bloqueadoPorAcreditacion && (
                      <Button size="sm" variant="outline" disabled
                        className="text-amber-700 border-amber-300"
                        data-testid={`start-quiz-blocked-${quiz.id}`}>
                        <Lock className="w-3.5 h-3.5 mr-1" /> Bloqueado
                      </Button>
                    )}
                    {!esCoordinadorODocente && quiz.mis_intentos > 0 && (
                      <Button size="sm" variant="outline" onClick={() => verResultados(quiz)}
                        data-testid={`my-results-quiz-${quiz.id}`}>
                        <BarChart3 className="w-3.5 h-3.5 mr-1" /> Mis intentos
                      </Button>
                    )}
                    {esCoordinadorODocente && (
                      <>
                        <Button size="sm" variant="outline" onClick={() => abrirBandejaCorreccion(quiz)}
                          data-testid={`correct-quiz-${quiz.id}`}
                          className="text-amber-700 border-amber-300 hover:bg-amber-50">
                          <Edit3 className="w-3.5 h-3.5 mr-1" /> Corregir
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => verResultados(quiz)}
                          data-testid={`results-quiz-${quiz.id}`}>
                          <BarChart3 className="w-3.5 h-3.5 mr-1" /> Resultados
                        </Button>
                        <button onClick={() => iniciarEdicionQuiz(quiz)}
                          className="text-gray-400 hover:text-violet-600 p-1"
                          title="Editar examen"
                          data-testid={`edit-quiz-${quiz.id}`}>
                          <Edit3 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => toggleEstadoQuiz(quiz)}
                          className={quiz.estado === 'cerrada'
                            ? 'text-emerald-500 hover:text-emerald-700 p-1'
                            : 'text-gray-400 hover:text-red-600 p-1'}
                          title={quiz.estado === 'cerrada' ? 'Reabrir examen' : 'Cerrar examen (no acepta nuevos intentos)'}
                          data-testid={`toggle-estado-quiz-${quiz.id}`}>
                          {quiz.estado === 'cerrada'
                            ? <Unlock className="w-4 h-4" />
                            : <Lock className="w-4 h-4" />}
                        </button>
                        <button onClick={() => eliminarQuiz(quiz.id)}
                          className="text-gray-300 hover:text-red-500 p-1"
                          title="Eliminar examen">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })
      )}

      {enviarEmailModalJSX}
    </div>
  );
}
